<script setup>
import { ref } from 'vue';
import { ElMessage } from 'element-plus';
import InvoiceService from '@/api/services/invoice.service';
import ClientService from '@/api/services/clients';

const emit = defineEmits(['closeDialog', 'done']);

// --- Sync Holded deshabilitado temporalmente ---
const syncingHolded = ref(false);
const syncingContacts = ref(false);
const holdedFrom = ref(new Date(new Date().getFullYear(), 0, 1).toISOString().substring(0, 10));
const holdedTo = ref(new Date().toISOString().substring(0, 10));
const holdedResult = ref(null);
const contactsResult = ref(null);

const disableHoldedToDates = (date) => {
    if (!holdedFrom.value) return false;
    return new Date(date) < new Date(holdedFrom.value);
};

const onHoldedFromChange = (value) => {
    if (holdedTo.value && value > holdedTo.value) holdedTo.value = value;
};

const doSyncHolded = () => {
    syncingHolded.value = true;
    holdedResult.value = null;
    InvoiceService.syncHolded(holdedFrom.value, holdedTo.value)
        .then((res) => {
            holdedResult.value = res.data;
            ElMessage.success(`Sincronización completada: ${res.data.created} creadas, ${res.data.updated} actualizadas`);
        })
        .catch((err) => {
            ElMessage.error(err.response?.data?.detail ?? 'Error al sincronizar con Holded');
        })
        .finally(() => { syncingHolded.value = false; });
};

const doSyncContacts = () => {
    syncingContacts.value = true;
    contactsResult.value = null;
    ClientService.syncHoldedContacts()
        .then((res) => {
            contactsResult.value = res.data;
            ElMessage.success(`Clientes sincronizados: ${res.data.linked} vinculados, ${res.data.updated} actualizados`);
        })
        .catch((err) => {
            ElMessage.error(err.response?.data?.detail ?? 'Error al sincronizar clientes');
        })
        .finally(() => { syncingContacts.value = false; });
};
// --- Fin bloque Holded deshabilitado ---

const dragging = ref(false);
const selectedFile = ref(null);
const loading = ref(false);
const result = ref(null);

const onDrop = (event) => {
    dragging.value = false;
    const file = event.dataTransfer.files[0];
    if (file) setFile(file);
};

const onFileChange = (event) => {
    const file = event.target.files[0];
    if (file) setFile(file);
};

const setFile = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
        ElMessage.error('Solo se admiten archivos .xlsx o .xls');
        return;
    }
    selectedFile.value = file;
    result.value = null;
};

const doImport = () => {
    if (!selectedFile.value) return;
    loading.value = true;
    result.value = null;
    InvoiceService.importFromExcel(selectedFile.value)
        .then((res) => {
            result.value = res.data;
            ElMessage.success(`Importación completada: ${res.data.created} creadas, ${res.data.updated} actualizadas`);
        })
        .catch((err) => {
            const msg = err.response?.data?.detail ?? err.response?.data?.message ?? 'Error al importar el archivo';
            ElMessage.error(msg);
        })
        .finally(() => { loading.value = false; });
};

</script>

<template>
    <div>
        <!-- Cabecera -->
        <div class="header">
            <svg style="width:30px;margin-bottom:14px;" viewBox="0 0 24 24" fill="none" stroke="#474ce6"
                stroke-width="1.8" aria-hidden="true">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <path d="M8 7h8M8 11h8M8 15h5" />
            </svg>
            <span class="header-title pl-4">Importar / Sincronizar facturas</span>
        </div>

        <!-- Sección 1: Excel -->
        <div class="section-label">Importar desde Excel</div>
        <div
            class="drop-zone"
            :class="{ dragging }"
            @dragover.prevent="dragging = true"
            @dragleave="dragging = false"
            @drop.prevent="onDrop"
            @click="$refs.fileInput.click()"
        >
            <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                aria-hidden="true">
                <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                <polyline points="16 12 12 8 8 12" />
                <line x1="12" y1="8" x2="12" y2="16" />
            </svg>
            <div v-if="selectedFile">
                <strong>{{ selectedFile.name }}</strong>
                <div class="text-muted small">{{ (selectedFile.size / 1024).toFixed(1) }} KB</div>
            </div>
            <div v-else class="text-muted">
                Arrastra aquí el archivo .xlsx o haz clic para seleccionarlo
            </div>
            <input ref="fileInput" type="file" accept=".xlsx,.xls" class="d-none" @change="onFileChange" />
        </div>
        <div class="section-actions">
            <button class="btn btn-block btn_red action-btn" :disabled="!selectedFile || loading || result !== null" @click="doImport">
                {{ loading ? 'Importando…' : 'Importar Excel' }}
            </button>
        </div>
        <div v-if="result" class="result-box">
            <div><span class="result-label">Creadas:</span> {{ result.created }}</div>
            <div><span class="result-label">Actualizadas:</span> {{ result.updated }}</div>
            <div v-if="result.unresolved && result.unresolved.length > 0">
                <span class="result-label unresolved-label">Sin resolver ({{ result.unresolved.length }}):</span>
                <ul class="mb-0 mt-1">
                    <li v-for="name in result.unresolved" :key="name" class="small unresolved-label">{{ name }}</li>
                </ul>
            </div>
        </div>

        <!-- Sección 2 y 3: Holded sync — deshabilitado temporalmente (v-if="false") -->
        <template v-if="false">
            <div class="divider" />

            <div class="section-label">Sincronizar facturas desde Holded</div>
            <div class="row">
                <div class="col-12 col-sm-6 mb-2">
                    <label class="w-100">Desde</label>
                    <el-date-picker v-model="holdedFrom" type="date" format="DD/MM/YYYY" value-format="YYYY-MM-DD"
                        class="w-100" :first-day-of-week="1" @change="onHoldedFromChange" />
                </div>
                <div class="col-12 col-sm-6 mb-2">
                    <label class="w-100">Hasta</label>
                    <el-date-picker v-model="holdedTo" type="date" format="DD/MM/YYYY" value-format="YYYY-MM-DD"
                        class="w-100" :first-day-of-week="1" :disabled-date="disableHoldedToDates" />
                </div>
            </div>
            <div class="section-actions">
                <button class="btn btn-block btn_red action-btn" :disabled="syncingHolded" @click="doSyncHolded">
                    {{ syncingHolded ? 'Sincronizando…' : 'Sincronizar facturas' }}
                </button>
            </div>
            <div v-if="holdedResult" class="result-box">
                <div><span class="result-label">Creadas:</span> {{ holdedResult.created }}</div>
                <div><span class="result-label">Actualizadas:</span> {{ holdedResult.updated }}</div>
                <div v-if="holdedResult.unresolved && holdedResult.unresolved.length > 0">
                    <span class="result-label unresolved-label">Sin resolver ({{ holdedResult.unresolved.length }}):</span>
                    <ul class="mb-0 mt-1">
                        <li v-for="name in holdedResult.unresolved" :key="name" class="small unresolved-label">{{ name }}</li>
                    </ul>
                </div>
            </div>

            <div class="divider" />

            <div class="section-label">Sincronizar clientes con Holded</div>
            <p class="text-muted small mb-3">
                Cruza los clientes de ConnectUS con los contactos de Holded para rellenar automáticamente nombre fiscal e ID.
            </p>
            <div class="section-actions">
                <button class="btn btn-block btn_red action-btn" :disabled="syncingContacts" @click="doSyncContacts">
                    {{ syncingContacts ? 'Sincronizando…' : 'Sincronizar clientes' }}
                </button>
            </div>
            <div v-if="contactsResult" class="result-box">
                <div><span class="result-label">Contactos Holded:</span> {{ contactsResult.total }}</div>
                <div><span class="result-label">Vinculados:</span> {{ contactsResult.linked }}</div>
                <div><span class="result-label">Actualizados:</span> {{ contactsResult.updated }}</div>
                <div><span class="result-label text-muted">Sin match:</span> {{ contactsResult.notFound }}</div>
            </div>
        </template>

        <!-- Footer -->
        <div class="row footer">
            <div class="col-md-12 mt-4" style="margin-left: -10px;">
                <div class="d-flex justify-content-end gap-3">
                    <div class="col-md-3">
                        <button class="btn btn-block btn_yellow edit-button" @click="result ? emit('done') : emit('closeDialog')">
                            {{ result ? 'Cerrar' : 'Cancelar' }}
                        </button>
                    </div>
                    <div class="col-md-4" v-if="result">
                        <button class="btn btn-block btn_red edit-button" @click="emit('done')">
                            Mostrar lista
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header-title {
    font-size: 30px;
    margin-left: 5px;
    font-weight: 800;
    letter-spacing: -0.05em;
    color: #474ce6;
}

.section-label {
    font-weight: 600;
    font-size: 14px;
    color: #303133;
    margin-bottom: 10px;
}

.divider {
    border-top: 1px solid #ebeef5;
    margin: 28px 0 20px;
}

.drop-zone {
    border: 2px dashed #c0c4cc;
    border-radius: 12px;
    padding: 28px 20px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    &.dragging,
    &:hover {
        border-color: #474ce6;
        background: #f4f4ff;
    }
}

.upload-icon {
    width: 36px;
    height: 36px;
    color: #474ce6;
}

.section-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
    margin-bottom: 4px;

    .action-btn {
        min-width: 180px;
    }

    @media (max-width: 575px) {
        justify-content: stretch;

        .action-btn {
            width: 100%;
        }
    }
}

.result-box {
    background: #f4f9f4;
    border: 1px solid #b7d9b7;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 13px;
    line-height: 1.8;
    margin-top: 10px;
}

.result-label {
    font-weight: 600;
}

.unresolved-label {
    color: #e6a23c;
}
</style>
